import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortalAccountState } from '@brimo/shared';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import type { PortalOverview } from '../portal/types';
import { contactKeys } from './api';

/**
 * The CRM counterparts of the client portal (Mandantenportal 27–29, docs/dev/api-portal.md „Staff routes",
 * api-portal-assistant.md „CRM counterparts", api-documents.md „Portal (M7)"). Every query lives under the
 * contact's root (`['contact', id, 'portal']`), so `portal.updated` and `contact.updated` refresh it.
 */

export interface PortalActionState {
  allowed: boolean;
  reason: string | null;
}

export interface PortalChangeRequest {
  id: string;
  fieldLabel: string;
  oldValue: string;
  newValue: string;
  submittedLabel: string;
}

export interface StaffPortalCard {
  status: {
    state: PortalAccountState;
    /** „Kein Zugang" · „Eingeladen am …" · „Aktiv seit 03.09.2026" · „Gesperrt seit …" */
    label: string;
    lastLogin: string | null;
    openTasks: number;
  };
  actions: {
    invite: PortalActionState;
    lock: PortalActionState;
    unlock: PortalActionState;
    preview: PortalActionState;
  };
  changeRequests: PortalChangeRequest[];
  rule: { text: string; activeDeal: string; lifecycle: string; ownedUnits: string };
  documents: {
    summary: string;
    customerUploads: { id: string; name: string; meta: string }[];
    openPortalRequests: { id: string; title: string }[];
  };
}

export interface ChangeRequestDecision {
  id: string;
  status: 'applied' | 'rejected';
  /** The financial hint „Übernommen. Das Volumen ändert sich erst mit einer neuen Selbstauskunft." or null. */
  message: string | null;
  taskCompleted: boolean;
}

export const portalKeys = {
  card: (contactId: string) => [...queryRoots.contact, contactId, 'portal'] as const,
  overview: (contactId: string) => [...queryRoots.contact, contactId, 'portal', 'overview'] as const,
};

export function usePortalCard(contactId: string, enabled: boolean) {
  return useQuery({
    queryKey: portalKeys.card(contactId),
    queryFn: ({ signal }) => api<StaffPortalCard>(`/contacts/${contactId}/portal`, { signal }),
    enabled,
    staleTime: 15_000,
  });
}

/** „Portalansicht öffnen": the customer's overview, read-only. */
export function usePortalOverview(contactId: string, enabled: boolean) {
  return useQuery({
    queryKey: portalKeys.overview(contactId),
    queryFn: ({ signal }) => api<PortalOverview>(`/contacts/${contactId}/portal/overview`, { signal }),
    enabled,
    staleTime: 0,
  });
}

export function invitePortal(contactId: string): Promise<StaffPortalCard> {
  return api<StaffPortalCard>(`/contacts/${contactId}/portal/invite`, { method: 'POST', body: {} });
}

export function lockPortal(contactId: string): Promise<StaffPortalCard> {
  return api<StaffPortalCard>(`/contacts/${contactId}/portal/lock`, { method: 'POST', body: {} });
}

export function unlockPortal(contactId: string): Promise<StaffPortalCard> {
  return api<StaffPortalCard>(`/contacts/${contactId}/portal/unlock`, { method: 'POST', body: {} });
}

export function applyChangeRequest(id: string): Promise<ChangeRequestDecision> {
  return api<ChangeRequestDecision>(`/portal-change-requests/${id}/apply`, { method: 'POST', body: {} });
}

export function rejectChangeRequest(id: string, note: string): Promise<ChangeRequestDecision> {
  return api<ChangeRequestDecision>(`/portal-change-requests/${id}/reject`, {
    method: 'POST',
    body: { note },
  });
}

/** „Bestätigen" of a chat appointment request (timeline entry and „Steht an"). */
export function confirmAppointmentRequest(appointmentId: string): Promise<void> {
  return api<void>(`/appointments/${appointmentId}/confirm-request`, { method: 'POST', body: {} });
}

/** „Notiz bearbeiten" of a note of the BRIMO assistant. */
export function editAssistantNote(
  contactId: string,
  eventId: string,
  text: string,
): Promise<{ id: string; text: string }> {
  return api(`/contacts/${contactId}/assistant-notes/${eventId}`, { method: 'PATCH', body: { text } });
}

/** „Im Portal sichtbar" of a document row (Portal 29). */
export function setDocumentVisible(documentId: string, visibleToCustomer: boolean): Promise<unknown> {
  return api<unknown>(`/documents/${documentId}`, { method: 'PATCH', body: { visibleToCustomer } });
}

/** Refreshes the portal card, the record (timeline, „Steht an") and the documents of a contact. */
export function useInvalidatePortal(): (contactId: string) => Promise<void> {
  const queryClient = useQueryClient();
  return async (contactId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: contactKeys.record(contactId) }),
      queryClient.invalidateQueries({ queryKey: contactKeys.documents(contactId) }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.tasks] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.session] }),
    ]);
  };
}
